/**
 * App-level host for the Update Schedule flow so it overlays WHATEVER view is
 * mounted — clicking the header badge (or Export's "Update Schedule" button)
 * opens the review here instead of yanking the user to the Export page.
 *
 * Flow: idle → reviewing (UpdateScheduleModal: overdue + in-progress, commits
 * the statusing) → simulating (CascadeWorkbenchModal: previews/commits the
 * downstream reschedule). onApplied lets a visible Export page refresh its
 * diff/changelog.
 */
import React, { useCallback, useEffect, useState } from "react";
import CascadeWorkbenchModal from "../../shared/cascadeWorkbench/CascadeWorkbenchModal/CascadeWorkbenchModal";
import UpdateScheduleModal from "../UpdateScheduleModal/UpdateScheduleModal";
import { useActivityLookups } from "../../takt/TaktBoardPage/useActivityLookups";
import {
  fetchActivityWorkCodes,
  fetchTaktBoard,
  fetchV2Schedules,
  trackEvent,
  workbenchApply,
} from "../../../api/clientV2";
import { emitF9Refresh } from "../../../api/scheduleMutations";
import type { TaktBoardResponse, WorkbenchBody } from "../../../models";

interface Props {
  /** Refetch the active schedule + board whenever this changes (version switch). */
  activeVersion: number | null;
  /** When true, open the review modal (overlays the current view). */
  open: boolean;
  /** Dismissed or finished — App clears its open flag. */
  onClose: () => void;
  /** A forward pass applied (or statusing committed) — refresh a visible Export page. */
  onApplied?: () => void;
}

const UpdateScheduleHost: React.FC<Props> = ({ activeVersion, open, onClose, onApplied }) => {
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [boardData, setBoardData] = useState<TaktBoardResponse | null>(null);
  // Complete activity -> work code for the WHOLE schedule, so the full-schedule
  // cascade colors every bar (the takt board only covers its own rows).
  const [fullWorkCodeMap, setFullWorkCodeMap] = useState<Map<number, string>>(new Map());
  // idle -> reviewing (overdue + auto-actuals tabs) -> simulating (cascade).
  const [stage, setStage] = useState<"idle" | "reviewing" | "simulating">("idle");
  // Once the cascade has been reached, the review must NOT auto-skip again (the
  // statusing is committed, so on "← Back" the review is empty — show it with an
  // "up to date" state rather than bouncing straight back to the cascade).
  const [visitedCascade, setVisitedCascade] = useState(false);

  useEffect(() => {
    fetchV2Schedules().then((schedules) => {
      setScheduleId(schedules.length > 0 ? schedules[0].id : null);
    });
  }, [activeVersion]);

  // The board + whole-schedule work-code map are only needed for the cascade
  // stage, so fetch them when the flow opens — not eagerly on every version
  // switch (this host is always mounted at the App level).
  useEffect(() => {
    if (!open || scheduleId == null) return;
    fetchTaktBoard(scheduleId).then(setBoardData).catch(() => setBoardData(null));
    fetchActivityWorkCodes(scheduleId)
      .then((m) => setFullWorkCodeMap(new Map(Object.entries(m).map(([k, v]) => [Number(k), v]))))
      .catch(() => setFullWorkCodeMap(new Map()));
  }, [open, scheduleId]);

  // Badge / button request → enter the review stage over the current view, and
  // force the F9-sync badge to recompute (the forward pass runs now, so the
  // badge should reflect it). A parent-driven close (e.g. version switch) flips
  // open false → tear the flow down so no stage lingers against a stale version.
  useEffect(() => {
    if (open) {
      setStage("reviewing");
      setVisitedCascade(false);
      emitF9Refresh();
      void trackEvent("f9_simulate_opened");
    } else {
      setStage("idle");
      setVisitedCascade(false);
    }
  }, [open]);

  const { workCodeMap, sectionMap, zoneMap } = useActivityLookups(boardData);
  // The cascade spans the whole schedule — prefer the complete work-code map so
  // every bar is colored; fall back to the board map if the fetch hasn't landed.
  const cascadeWorkCodeMap = fullWorkCodeMap.size ? fullWorkCodeMap : workCodeMap;

  const close = useCallback(() => {
    setStage("idle");
    onClose();
  }, [onClose]);

  const handleProceed = useCallback(() => {
    setVisitedCascade(true);
    setStage("simulating");
  }, []);

  const handleApply = useCallback(
    (body: WorkbenchBody) => workbenchApply(scheduleId!, body),
    [scheduleId],
  );

  const handleApplied = useCallback(() => {
    close();
    onApplied?.();
  }, [close, onApplied]);

  if (scheduleId == null) return null;

  return (
    <>
      {stage === "reviewing" && (
        <UpdateScheduleModal
          scheduleId={scheduleId}
          allowAutoSkip={!visitedCascade}
          onCancel={close}
          onProceed={handleProceed}
          onUpdated={onApplied}
        />
      )}

      {stage === "simulating" && boardData != null && (
        <CascadeWorkbenchModal
          scheduleId={scheduleId}
          source={null}
          replanAll
          headerLabel="Update Schedule — new dates from the data date"
          colors={boardData.colors}
          activityWorkCodeMap={cascadeWorkCodeMap}
          sectionMap={sectionMap}
          zoneMap={zoneMap}
          onBack={() => setStage("reviewing")}
          onCancel={close}
          onApply={handleApply}
          onApplied={handleApplied}
        />
      )}
    </>
  );
};

export default UpdateScheduleHost;
